/**
 * StarAPI — PRIMARY Instagram data provider
 *
 * RapidAPI: https://rapidapi.com/StarAPI/api/starapi1
 * Host:     starapi1.p.rapidapi.com
 * Method:   All endpoints use POST with JSON body
 *
 * RAPIDAPI KEY: Uses KEY_D221 (only key) via per-host routing in rapidapi-keys.ts.
 *   - 511F account was lost on 2026-02-27. D221 is the sole key for all APIs now.
 *
 * SUPPORTED FEATURES:
 *   ✅ Profile lookup (by username)
 *   ✅ Username lookup (by Instagram ID)
 *   ✅ User search
 *   ✅ Hashtag search
 *   ✅ Similar accounts
 *   ✅ User followers
 *   ✅ Stories, Highlights, Posts (by Instagram ID)
 *   ❌ Places search (not available — falls through to next provider)
 *
 * PROVIDER CHAIN POSITION: 1st (primary) in instagram/index.ts strategies array.
 * If StarAPI fails, the system falls through to UpleapAPI, then IGFlashAPI.
 */

import type { FetchingStrategy, IGQueryTypes, InstagramHighlightProps, InstagramPostProps, InstagramProfileProps, InstagramQueryProps, InstagramStoryProps, StrategyName } from ".."
import { makeRapidApiRequest, type RapidApiRequestControls } from "../../rapidapi-keys"

const IG_QUERY_TYPES = {
    users: "users",
    hashtags: "hashtags",
    places: "places",
    similarUsers: "similarUsers"
} as const

const STARAPI_HOST = "starapi1.p.rapidapi.com"
const STARAPI_BASE_URL = "https://starapi1.p.rapidapi.com"

export default class StarAPI implements FetchingStrategy {
    public readonly strategyName: StrategyName = "StarAPI"

    private async makeRequest(
        endpoint: string,
        body: object,
        signal?: AbortSignal,
        controls?: RapidApiRequestControls
    ): Promise<any> {
        return await makeRapidApiRequest(
            `${STARAPI_BASE_URL}${endpoint}`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
                signal
            },
            STARAPI_HOST,
            "StarAPI",
            controls
        )
    }

    async fetchMediaByShortcode(shortcode: string, signal?: AbortSignal): Promise<unknown> {
        return await this.makeRequest(
            "/instagram/media/get_info_by_shortcode",
            { shortcode },
            signal,
            { maxAttempts: 1, logQuota: true }
        )
    }

    async fetchMediaById(mediaId: string, signal?: AbortSignal): Promise<unknown> {
        return await this.makeRequest(
            "/instagram/media/get_info",
            { id: mediaId },
            signal,
            { maxAttempts: 1, logQuota: true }
        )
    }

    async fetchProfile(username: string, includePosts: boolean): Promise<any> {
        if (includePosts) throw new Error("StarAPI doesn't support fetching posts in profile endpoint")

        const data = await this.makeRequest("/instagram/user/get_web_profile_info", { username })
        return this.processResponse("fetchProfile", data)
    }

    async fetchUsernameByInstagramID(instagramID: string): Promise<string | boolean> {
        const data = await this.makeRequest("/instagram/user/get_info_by_id", { id: instagramID })

        if (data.status === "done" && data.response?.status_code === 200 && data.response?.body?.user?.username) {
            return data.response.body.user.username
        }
        return false
    }

    async fetchInstagramQuery(query: string, queryType: string, limit: number = 15, attemptNumber: number = 0, backup: boolean = false): Promise<boolean | InstagramProfileProps | [InstagramQueryProps] | { error: string }> {
        let data

        if (queryType === IG_QUERY_TYPES.users) {
            data = await this.makeRequest("/instagram/user/search", { query })
        } else if (queryType === IG_QUERY_TYPES.hashtags) {
            data = await this.makeRequest("/instagram/hashtag/search", { query })
        } else if (queryType === IG_QUERY_TYPES.places) {
            // StarAPI doesn't have a places endpoint, throw to fallback to next strategy
            throw new Error("StarAPI doesn't support places search")
        } else {
            throw new Error(`Unknown query type: ${queryType}`)
        }

        console.log(`[StarAPI] fetchInstagramQuery response for ${queryType}:`, JSON.stringify(data).substring(0, 500))
        return this.processResponse("fetchInstagramQuery", data, queryType as IGQueryTypes)
    }

    async fetchSimilarInstagramAccounts(instagramId: string, limit: number = 15): Promise<boolean | InstagramProfileProps | [InstagramQueryProps] | { error: string }> {
        const data = await this.makeRequest("/instagram/user/get_similar_accounts", { id: instagramId })
        console.log(`[StarAPI] fetchSimilarInstagramAccounts(${instagramId}, ${limit}):`, JSON.stringify(data).substring(0, 500))
        return this.processResponse("fetchSimilarInstagramAccounts", data, IG_QUERY_TYPES.similarUsers)
    }

    async fetchUserFollowers(instagramId: string, limit: number = 15): Promise<boolean | InstagramProfileProps | [InstagramQueryProps] | { error: string }> {
        const data = await this.makeRequest("/instagram/user/get_followers", {
            id: instagramId,
            count: limit,
            max_id: null
        })
        return this.processResponse("fetchUserFollowers", data, IG_QUERY_TYPES.similarUsers)
    }

    async fetchStoriesByInstagramID(instagramId: string): Promise<{ profile: InstagramProfileProps; stories: InstagramStoryProps[] }> {
        const data = await this.makeRequest("/instagram/user/get_stories", { ids: [parseInt(instagramId)] })
        console.log(`[StarAPI] fetchStoriesByInstagramID response:`, JSON.stringify(data).substring(0, 500))

        if (data.status !== "done" || data.response?.status_code !== 200) {
            throw new Error(`Failed to fetch stories from StarAPI: ${data.response?.body?.status || "Unknown error"}`)
        }

        const profile: InstagramProfileProps = data.response.body.user
            ? {
                  graphql: {
                      user: {
                          ...data.response.body.user,
                          id: instagramId
                      }
                  }
              }
            : false

        const reelData = data.response.body.reels?.[instagramId]
        if (!reelData) {
            console.log(`[StarAPI] No stories found for Instagram ID: ${instagramId}`)
            return { profile: profile, stories: [] }
        }

        const stories: InstagramStoryProps[] =
            reelData.items?.map((item) => ({
                id: item.id,
                taken_at: item.taken_at,
                media_type: item.media_type,
                shortcode: item.code,
                image_url: item.image_versions2?.candidates?.[0]?.url,
                video_url: item.video_versions ? item.video_versions[0].url : null,
                video_view_count: item.view_count || 0,
                accessibility_caption: item.accessibility_caption || null
            })) || []

        return { profile, stories }
    }

    async fetchHighlightsByInstagramID(instagramId: string): Promise<{ profile: InstagramProfileProps; highlights: InstagramHighlightProps[] }> {
        const data = await this.makeRequest("/instagram/user/get_highlights", { id: parseInt(instagramId) })

        if (data.status !== "done" || data.response?.status_code !== 200) {
            throw new Error(`Failed to fetch highlights from StarAPI: ${data.response?.body?.status || "Unknown error"}`)
        }

        const profile: InstagramProfileProps = data.response.body.user
            ? {
                  graphql: {
                      user: {
                          ...data.response.body.user,
                          id: instagramId
                      }
                  }
              }
            : false

        const highlightsData = data.response.body.data?.user?.edge_highlight_reels?.edges
        if (!highlightsData || highlightsData.length === 0) {
            console.log(`[StarAPI] No highlights found for Instagram ID: ${instagramId}`)
            return { profile: profile, highlights: [] }
        }

        const highlights: InstagramHighlightProps[] = highlightsData.map((edge) => ({
            id: edge.node.id,
            title: edge.node.title,
            cover_media_url: edge.node.cover_media?.thumbnail_src,
            items: []
        }))

        return { profile, highlights }
    }

    async fetchPostsByInstagramID(instagramId: string): Promise<{ profile: InstagramProfileProps; posts: InstagramPostProps[] }> {
        const data = await this.makeRequest("/instagram/user/get_media", { id: parseInt(instagramId), count: 12, max_id: null })

        if (data.status !== "done" || data.response?.status_code !== 200) {
            throw new Error(`Failed to fetch posts from StarAPI: ${data.response?.body?.status || "Unknown error"}`)
        }

        const profile: InstagramProfileProps = data.response.body.user
            ? {
                  graphql: {
                      user: {
                          ...data.response.body.user,
                          id: instagramId
                      }
                  }
              }
            : false

        const postsData = data.response.body.items
        if (!postsData || postsData.length === 0) {
            console.log(`[StarAPI] No posts found for Instagram ID: ${instagramId}`)
            return { profile: profile, posts: [] }
        }

        console.log(`[StarAPI] Fetched profile for ${instagramId}`)

        const posts: InstagramPostProps[] = postsData.map((post) => ({
            id: post.code,
            caption: post.caption?.text || null,
            media_type: post.media_type,
            image_url: post.image_versions2?.candidates?.[0]?.url || null,
            video_url: post.video_versions ? post.video_versions[0].url : null,
            like_count: post.like_count,
            comment_count: post.comment_count,
            taken_at: post.taken_at,
            is_video: post.media_type === 2,
            accessibility_caption: post.accessibility_caption || null,
            is_album: post.media_type === 8,
            album_children: post.carousel_media
                ? post.carousel_media.map((child) => ({
                      id: child.id,
                      image_url: child.image_versions2?.candidates?.[0]?.url || null,
                      is_video: child.media_type === 2,
                      video_url: child.video_versions ? child.video_versions[0].url : null,
                      accessibility_caption: child.accessibility_caption || null
                  }))
                : null
        }))

        return { profile, posts }
    }

    async processResponse(caller: string, response: any, queryType?: IGQueryTypes): Promise<[InstagramQueryProps] | InstagramProfileProps | { error: string } | boolean> {
        let results: any

        // Guard against undefined response.response (API error, rate limit, network issue)
        if (!response?.response) {
            throw new Error(`Failed to fetch data from StarAPI: ${response?.message || response?.status || "No response received"}`)
        }

        if (response.status === "done" && response.response?.status_code === 404) return false

        if (response.status !== "done" || response.response?.status_code !== 200) throw new Error(`Failed to fetch data from StarAPI: ${response.message || response.response?.status_message || "Unknown error"}`)

        if (caller === "fetchProfile") {
            const user = response.response.body?.data?.user
            if (!user || !user.id) {
                console.log(`[StarAPI] Username doesn't exist.`)
                return false
            }

            results = {
                graphql: {
                    user: {
                        id: user.id,
                        username: user.username,
                        full_name: user.full_name,
                        biography: user.biography,
                        category: user.category_name || user.category_enum,
                        external_url: user.external_url,
                        external_url_title: user.bio_links?.[0]?.title || null,
                        profile_pic_url: user.profile_pic_url,
                        profile_pic_url_hd: user.profile_pic_url_hd || user.profile_pic_url,
                        follower_count: user.edge_followed_by?.count,
                        following_count: user.edge_follow?.count,
                        media_count: user.edge_owner_to_timeline_media?.count,
                        is_private: user.is_private,
                        is_verified: user.is_verified,
                        is_business: user.is_business_account,
                        is_professional: user.is_professional_account,
                        edge_followed_by: user.edge_followed_by,
                        edge_follow: user.edge_follow,
                        edge_owner_to_timeline_media:
                            user.edge_owner_to_timeline_media?.edges?.map((edge) => ({
                                id: edge.node.shortcode,
                                caption: edge.node.edge_media_to_caption?.edges?.[0]?.node?.text,
                                location: edge.node.location?.name || null,
                                image_url: edge.node.display_url,
                                hd_image_url: edge.node.thumbnail_resources?.[edge.node.thumbnail_resources.length - 1]?.src,
                                like_count: edge.node.edge_liked_by?.count,
                                comment_count: edge.node.edge_media_to_comment?.count,
                                taken_at: edge.node.taken_at_timestamp,
                                accessibility_caption: edge.node.accessibility_caption || null,
                                is_video: edge.node.is_video,
                                video_view_count: edge.node.video_view_count || null,
                                is_album: !!edge.node.edge_sidecar_to_children,
                                album_children:
                                    edge.node.edge_sidecar_to_children?.edges?.map((child: any) => ({
                                        id: child.node.shortcode,
                                        image_url: child.node.display_url,
                                        is_video: child.node.is_video,
                                        video_url: child.node.video_url || null,
                                        video_view_count: child.node.video_view_count || null,
                                        accessibility_caption: child.node.accessibility_caption || null
                                    })) || null
                            })) || []
                    }
                }
            }
        } else if (caller === "fetchInstagramQuery") {
            switch (queryType) {
                case IG_QUERY_TYPES.users:
                    if (response.response.body?.users) {
                        results = response.response.body.users.map((user: any) => ({
                            full_name: user.full_name,
                            avatar_url: user.profile_pic_url,
                            profile_pic_url: user.profile_pic_url,
                            username: user.username,
                            is_verified: user.is_verified,
                            is_private: user.is_private,
                            link: `instagram/${user.username}/`,
                            type: "ig",
                            user_id: user.pk || user.id,
                            follower_count: 0
                        }))
                    }
                    break
                case IG_QUERY_TYPES.hashtags:
                    if (response.response.body?.hashtags) {
                        results = response.response.body.hashtags.map((hashtag: any) => ({
                            name: hashtag.name,
                            profile_pic_url: hashtag.profile_pic_url,
                            target_id: hashtag.id,
                            mediaCount: hashtag.media_count,
                            search_result_subtitle: hashtag.search_result_subtitle
                        }))
                    }
                    break
                case IG_QUERY_TYPES.places:
                    // StarAPI doesn't support places search
                    throw new Error("StarAPI doesn't support places search")
            }
        } else if (caller === "fetchSimilarInstagramAccounts") {
            if (response.status === "done" && response.response?.status_code === 200) {
                const edges = response.response.body?.data?.user?.edge_chaining?.edges
                if (edges) {
                    results = edges.map((edge: any) => ({
                        id: edge.node.id,
                        full_name: edge.node.full_name,
                        is_verified: edge.node.is_verified,
                        profile_pic_url: edge.node.profile_pic_url,
                        username: edge.node.username,
                        is_private: edge.node.is_private
                    }))
                }
            }
        } else if (caller === "fetchUserFollowers") {
            if (response.status === "done" && response.response?.status_code === 200) {
                const users = response.response.body?.users
                if (users) {
                    results = users.map((user: any) => ({
                        full_name: user.full_name,
                        avatar_url: user.profile_pic_url,
                        profile_pic_url: user.profile_pic_url,
                        username: user.username,
                        is_verified: user.is_verified,
                        is_private: user.is_private,
                        user_id: user.pk
                    }))
                }
            }
        }
        return results
    }
}
